import { apiFetch } from './apiFetch';

interface StoredAuthState {
  state: {
    accessToken: string | null;
    sessionToken: string | null;
    impersonateId: string | null;
    isSessionExpired: boolean | null;
  };
}

interface MeResponse {
  role: string;
  companyId: string;
}

async function fetchMeForAuthCheck(): Promise<MeResponse | null> {
  try {
    return await apiFetch<MeResponse>('/v3/me');
  } catch {
    return null;
  }
}

async function importKeyFromEnv(): Promise<CryptoKey | null> {
  const keyStr = import.meta.env.VITE_REDIRECT_ENCRYPTION_KEY;
  if (!keyStr) return null;
  const raw = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(keyStr),
  );
  return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['decrypt']);
}

interface TokenPayload {
  accessToken: string;
  sessionToken: string | null;
  impersonateId: string | null;
}

async function decryptTokenParam(
  encoded: string,
): Promise<TokenPayload | null> {
  try {
    const key = await importKeyFromEnv();
    if (!key) return null;
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const combined = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext,
    );
    return JSON.parse(new TextDecoder().decode(decrypted)) as TokenPayload;
  } catch {
    return null;
  }
}

function storeAuthTokens(
  accessToken: string,
  sessionToken: string | null,
  impersonateId: string | null,
): void {
  const stored = {
    state: {
      accessToken,
      sessionToken: sessionToken ?? null,
      impersonateId: impersonateId ?? null,
      isSessionExpired: false,
      auditCompanyId: null,
      companyId: null,
      refreshToken: null,
      mePreferencesUpdated: false,
    },
    version: 0,
  };
  localStorage.setItem('retraced:context', JSON.stringify(stored));
  history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search,
  );
}

async function seedFromHash(): Promise<void> {
  try {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const params = new URLSearchParams(hash);

    const encryptedToken = params.get('token');
    if (encryptedToken) {
      const payload = await decryptTokenParam(encryptedToken);
      if (payload?.accessToken) {
        storeAuthTokens(
          payload.accessToken,
          payload.sessionToken,
          payload.impersonateId,
        );
      }
      return;
    }

    // Legacy plain-text fallback (remove once old frontend fully migrated)
    const accessToken = params.get('accessToken');
    const sessionToken = params.get('sessionToken');
    const impersonateId = params.get('impersonateId');
    if (!accessToken) return;
    storeAuthTokens(accessToken, sessionToken, impersonateId);
  } catch {
    // ignore
  }
}

async function readStoredAuth(): Promise<StoredAuthState['state'] | null> {
  try {
    await seedFromHash();
    const raw = localStorage.getItem('retraced:context');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAuthState;
    return parsed.state ?? null;
  } catch {
    return null;
  }
}

function isAdmin(me: MeResponse): boolean {
  const adminCompanyId = import.meta.env.VITE_RETRACED_ADMIN_COMPANY_ID;
  return Boolean(
    me.role?.startsWith('RETRACED_') && me.companyId === adminCompanyId,
  );
}

interface AuthGuardResult {
  allowed: boolean;
  accessToken?: string;
  impersonateId?: string | null;
}

export async function checkAuthGuard(): Promise<AuthGuardResult> {
  const auth = await readStoredAuth();

  // Not authenticated
  if (!auth?.accessToken || auth.isSessionExpired) return { allowed: false };

  // Impersonating — always allow
  if (auth.impersonateId)
    return {
      allowed: true,
      accessToken: auth.accessToken,
      impersonateId: auth.impersonateId,
    };

  // Not impersonating — fetch /me to check if Retraced admin
  const me = await fetchMeForAuthCheck();
  if (!me) return { allowed: false };

  return {
    allowed: !isAdmin(me),
    accessToken: auth.accessToken,
    impersonateId: null,
  };
}
